import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { dossiersAPI, walletAPI } from '../services/api';

const API_URL = process.env.REACT_APP_API_URL || 'https://lexback-production.up.railway.app';

// Texte copiable en un clic (référence, n° de liasse, SIREN…)
function CopyText({ text, mono = false, style }) {
  const [copied, setCopied] = useState(false);
  if (text == null || text === '') return <span style={{ color: 'rgba(26,25,22,0.3)' }}>—</span>;
  const copy = (e) => {
    e.stopPropagation();
    try { navigator.clipboard.writeText(String(text)); } catch (_) {}
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  };
  return (
    <span onClick={copy} title="Cliquer pour copier"
      style={{ cursor: 'pointer', fontFamily: mono ? 'monospace' : 'inherit', display: 'inline-flex', alignItems: 'center', gap: '5px', ...style }}>
      {text}
      <span style={{ fontSize: '10px', color: copied ? '#1A7A4A' : 'rgba(26,25,22,0.25)' }}>{copied ? '✓ copié' : '⧉'}</span>
    </span>
  );
}

// ─── Cellule prix éditable inline (style Airtable) ───
// Saisie en euros HT, sauvegarde au blur ou à Entrée.
// Affiche "—" tant que vide.
function EditablePriceCell({ value, onSave, disabled }) {
  const [val, setVal] = useState(value == null || value === '' ? '' : String(value));
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    setVal(value == null || value === '' ? '' : String(value));
  }, [value]);

  const commit = async () => {
    setEditing(false);
    const trimmed = (val || '').toString().replace(',', '.').trim();
    const num = trimmed === '' ? null : Number(trimmed);
    if (num !== null && Number.isNaN(num)) {
      setVal(value == null || value === '' ? '' : String(value));
      return;
    }
    // Pas de changement réel → ne rien envoyer
    const currentNum = value == null || value === '' ? null : Number(value);
    if (num === currentNum) return;
    setSaving(true);
    try {
      await onSave(num);
    } catch (e) {
      console.error('Save tarif failed', e);
      setVal(value == null || value === '' ? '' : String(value));
      alert('Impossible d\'enregistrer le tarif');
    } finally {
      setSaving(false);
    }
  };

  if (disabled) {
    return (
      <span style={{ fontSize: '12px', color: 'rgba(26,25,22,0.5)', fontVariantNumeric: 'tabular-nums' }}>
        {value != null && value !== '' ? `${Number(value).toLocaleString('fr-FR')} €` : <span style={{ color: 'rgba(26,25,22,0.25)' }}>—</span>}
      </span>
    );
  }

  return (
    <div
      onClick={(e) => { e.stopPropagation(); if (!editing) setEditing(true); }}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: '4px',
        padding: '3px 7px', borderRadius: '5px',
        border: editing ? '1px solid #B8860B' : '0.5px solid transparent',
        background: editing ? '#fff' : 'transparent',
        cursor: 'text', minWidth: '70px',
      }}
      onMouseEnter={e => { if (!editing) e.currentTarget.style.background = '#f5f1ea'; }}
      onMouseLeave={e => { if (!editing) e.currentTarget.style.background = 'transparent'; }}
    >
      <input
        type="text"
        inputMode="decimal"
        value={val}
        placeholder="—"
        onChange={e => setVal(e.target.value)}
        onFocus={() => setEditing(true)}
        onBlur={commit}
        onKeyDown={e => {
          if (e.key === 'Enter') { e.currentTarget.blur(); }
          if (e.key === 'Escape') { setVal(value == null || value === '' ? '' : String(value)); setEditing(false); e.currentTarget.blur(); }
        }}
        style={{
          width: '60px',
          border: 'none', outline: 'none', background: 'transparent',
          fontSize: '12px', color: '#1A1916',
          fontFamily: 'inherit', fontVariantNumeric: 'tabular-nums',
          padding: 0,
        }}
      />
      <span style={{ fontSize: '11px', color: 'rgba(26,25,22,0.4)' }}>€</span>
      {saving && <span style={{ fontSize: '10px', color: '#B8860B' }}>…</span>}
    </div>
  );
}

const INPI_STATUS = {
  depose:         { label: 'En instruction', color: '#1A55A0', bg: 'rgba(26,85,160,0.08)' },
  en_cours:       { label: 'En instruction', color: '#1A55A0', bg: 'rgba(26,85,160,0.08)' },
  transmis_gtc:   { label: 'En instruction', color: '#1A55A0', bg: 'rgba(26,85,160,0.08)' },
  retransmis_gtc: { label: 'En instruction', color: '#1A55A0', bg: 'rgba(26,85,160,0.08)' },
  termine:        { label: 'Validé',         color: '#1A7A4A', bg: 'rgba(26,122,74,0.08)' },
  rejete:         { label: 'Rejeté',         color: '#B03020', bg: 'rgba(176,48,32,0.08)' },
  recu:           { label: 'À déposer',      color: '#d97706', bg: 'rgba(217,119,6,0.08)' },
  assigne:        { label: 'À déposer',      color: '#d97706', bg: 'rgba(217,119,6,0.08)' },
  doc_manquant:   { label: 'Bloqué',         color: '#B03020', bg: 'rgba(176,48,32,0.08)' },
};

const card = { background: '#fff', border: '0.5px solid rgba(0,0,0,0.07)', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' };

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch { return '—'; }
};

// Récupère le 1er document d'une catégorie depuis les attachments (Kbis/RBE)
const findAttByCategory = (attsMap, category) => {
  if (!attsMap) return null;
  const list = attsMap[category] || [];
  return list.length > 0 ? list[0] : null;
};

export default function SuiviINPI() {
  const navigate = useNavigate();
  const [dossiers, setDossiers] = useState([]);
  const [wallet, setWallet]     = useState(null);
  const [loading, setLoading]   = useState(true);
  const [filter, setFilter]     = useState('tous');
  // Attachments par dossier (chargé en background pour les onglets Validés et Rejetés)
  const [attsByDossier, setAttsByDossier] = useState({});

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [dr, wr] = await Promise.all([
        dossiersAPI.list({ limit: 200 }),
        walletAPI.get(),
      ]);
      setDossiers(dr.data || []);
      setWallet(wr.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // ── Charge les attachments des dossiers Validés + Rejetés en background ──
  // Pour les Validés on veut Kbis/RBE/Cession ; pour les Rejetés on veut les notes (côté dossier directement)
  useEffect(() => {
    if (dossiers.length === 0) return;
    let cancelled = false;
    const needed = dossiers.filter(d => d.status === 'termine' || d.status === 'rejete');
    if (needed.length === 0) return;
    const token = localStorage.getItem('lexback_token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    (async () => {
      // Batch par 5
      for (let i = 0; i < needed.length; i += 5) {
        const batch = needed.slice(i, i + 5);
        const results = await Promise.all(
          batch.map(d =>
            fetch(`${API_URL}/api/documents/attachments/${d.id}`, { headers })
              .then(r => r.ok ? r.json() : null)
              .then(json => ({ id: d.id, atts: json }))
              .catch(() => ({ id: d.id, atts: null }))
          )
        );
        if (cancelled) return;
        setAttsByDossier(prev => {
          const next = { ...prev };
          for (const r of results) {
            if (r.atts) next[r.id] = r.atts;
          }
          return next;
        });
      }
    })();
    return () => { cancelled = true; };
  }, [dossiers]);

  if (loading) return <div style={{ padding: '3rem', textAlign: 'center', color: 'rgba(26,25,22,0.4)', fontSize: '13px' }}>Chargement…</div>;

  // Sauvegarde optimiste d'un tarif sur un dossier (tarif_gtc ou tarif_jal)
  const handleSaveTarif = async (dossierId, field, value) => {
    // Optimistic update
    setDossiers(list => list.map(d => d.id === dossierId ? { ...d, [field]: value } : d));
    try {
      await dossiersAPI.update(dossierId, { [field]: value });
    } catch (e) {
      // Rollback en cas d'erreur
      setDossiers(list => list.map(d => d.id === dossierId ? { ...d, [field]: d[field] } : d));
      throw e;
    }
  };

  // Page INPI = uniquement les dossiers déjà transmis au Guichet de Tribunal de Commerce.
  // Les statuts "recu" / "assigne" / "en_cours" sont gérés en interne Lexback (pré-transmission)
  // et n'ont pas leur place dans Suivi INPI. Ils restent visibles sur la page Dossiers.
  const INPI_VISIBLE_STATUSES = [
    'transmis_gtc',       // transmis au GTC, en attente d'instruction
    'retransmis_gtc',     // retransmis après corrections
    'depose',             // déposé (statut legacy équivalent transmis_gtc)
    'termine',            // validé par le greffe
    'rejete',             // rejeté par le greffe
  ];

  const inpiDossiers = dossiers.filter(d =>
    !['depot_comptes'].includes(d.type) &&
    INPI_VISIBLE_STATUSES.includes(d.status)
  );

  // En instruction = uniquement transmis_gtc / retransmis_gtc (demande Sara 25/05)
  const enInstruction = inpiDossiers.filter(d =>
    ['transmis_gtc', 'retransmis_gtc', 'depose'].includes(d.status)
  );
  const valides       = inpiDossiers.filter(d => d.status === 'termine');
  const rejetes       = inpiDossiers.filter(d => d.status === 'rejete');
  // À déposer = vide sur la page INPI (logique repensée : "à déposer" est avant d'arriver à l'INPI)
  // On garde la variable pour ne rien casser, mais elle sera toujours vide ici.
  const aDeposer      = [];

  const filtered = filter === 'tous'        ? inpiDossiers
                 : filter === 'instruction' ? enInstruction
                 : filter === 'valide'      ? valides
                 : filter === 'rejete'      ? rejetes
                 : aDeposer;

  const walletBalance  = wallet ? Number(wallet.balance) : 0;
  const walletThreshold = wallet ? Number(wallet.alert_threshold) : 3000;
  const walletPct      = Math.min(100, Math.round((walletBalance / (walletThreshold * 3.8)) * 100));
  const walletOk       = walletBalance > walletThreshold;

  // Colonnes par onglet
  // Base : Référence, Société, Type, Date d'envoi (création), Déposé le, Statut, Tarif GTC, Tarif JAL, SLA
  // Validés : remplace SLA par Kbis + RBE
  // Rejetés : remplace SLA par "Motif rejet / Éléments manquants"
  // En instruction : standard (transmis_gtc / retransmis_gtc)
  const columnsFor = (f) => {
    const base = ['Référence', 'N° de liasse', 'Société', 'Type', 'Date d\'envoi', 'Déposé le', 'Statut INPI', 'Tarif GTC', 'Tarif JAL'];
    if (f === 'valide')  return [...base, 'Kbis', 'RBE', 'Actions'];
    if (f === 'rejete')  return [...base, 'Motif / Éléments manquants', 'Actions'];
    return [...base, 'SLA', 'Actions'];
  };
  const cols = columnsFor(filter);

  // Date d'envoi = date_envoi_inpi si présente, sinon submitted_at, sinon created_at
  const dateEnvoi = (d) => d.date_envoi_inpi || d.submitted_at || d.created_at;
  // Déposé le = date_depot_inpi si présente, sinon date_envoi_inpi
  const dateDepot = (d) => d.date_depot_inpi || d.deposed_at || d.date_envoi_inpi;

  // Récupère le motif de rejet OU les éléments manquants
  const motifRejet = (d) =>
    d.motif_rejet || d.motif_rejet_inpi || d.elements_manquants || '';

  // Bouton de téléchargement compact pour un attachment
  const DocCell = ({ att }) => {
    if (!att) return <span style={{ color: 'rgba(26,25,22,0.25)', fontSize: '11px' }}>—</span>;
    const url = att.download_url || att.url;
    return url ? (
      <a href={url} target="_blank" rel="noreferrer" style={{
        display: 'inline-flex', alignItems: 'center', gap: '4px',
        padding: '3px 8px', borderRadius: '4px',
        background: 'rgba(26,122,74,0.08)', color: '#1A7A4A',
        fontSize: '11px', fontWeight: 500, textDecoration: 'none',
        border: '0.5px solid rgba(26,122,74,0.18)',
      }}>
        ⬇ PDF
      </a>
    ) : (
      <span style={{ color: 'rgba(26,25,22,0.45)', fontSize: '11px' }}>✓</span>
    );
  };

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
        <div>
          <div style={{ fontFamily: 'Syne, sans-serif', fontSize: '18px', fontWeight: 600, color: '#1A1916' }}>Suivi INPI</div>
          <div style={{ fontSize: '12px', color: 'rgba(26,25,22,0.4)', marginTop: '2px' }}>Statuts en temps réel — Guichet Unique</div>
        </div>
      </div>

      {/* KPIs + mini wallet — page INPI = uniquement les dossiers transmis au GTC */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 280px', gap: '10px', marginBottom: '1.25rem' }}>
        <div style={{ ...card, padding: '14px 16px', cursor: 'pointer' }} onClick={() => setFilter('instruction')}>
          <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.07em', color: 'rgba(26,25,22,0.28)', marginBottom: '4px' }}>En instruction</div>
          <div style={{ fontFamily: 'Syne, sans-serif', fontSize: '24px', fontWeight: 700, color: '#1A55A0' }}>{enInstruction.length}</div>
        </div>
        <div style={{ ...card, padding: '14px 16px', cursor: 'pointer' }} onClick={() => setFilter('valide')}>
          <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.07em', color: 'rgba(26,25,22,0.28)', marginBottom: '4px' }}>Validés ce mois</div>
          <div style={{ fontFamily: 'Syne, sans-serif', fontSize: '24px', fontWeight: 700, color: '#1A7A4A' }}>{valides.length}</div>
        </div>
        <div style={{ ...card, padding: '14px 16px', cursor: 'pointer' }} onClick={() => setFilter('rejete')}>
          <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.07em', color: 'rgba(26,25,22,0.28)', marginBottom: '4px' }}>Rejetés</div>
          <div style={{ fontFamily: 'Syne, sans-serif', fontSize: '24px', fontWeight: 700, color: rejetes.length > 0 ? '#B03020' : '#1A1916' }}>{rejetes.length}</div>
        </div>

        {/* Mini wallet card */}
        <div onClick={() => navigate('/wallet')} style={{ ...card, padding: '14px 16px', cursor: 'pointer', background: walletOk ? '#fff' : 'rgba(184,134,11,0.03)', border: walletOk ? '0.5px solid rgba(0,0,0,0.07)' : '0.5px solid rgba(184,134,11,0.2)' }}>
          <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.07em', color: 'rgba(26,25,22,0.28)', marginBottom: '6px' }}>Wallet INPI</div>
          <div style={{ fontFamily: 'Syne, sans-serif', fontSize: '20px', fontWeight: 700, color: walletOk ? '#1A7A4A' : '#d97706', marginBottom: '6px' }}>
            {walletBalance.toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €
          </div>
          <div style={{ height: '3px', background: '#F0EDE5', borderRadius: '2px', overflow: 'hidden', marginBottom: '5px' }}>
            <div style={{ width: `${walletPct}%`, height: '100%', background: walletOk ? '#1A7A4A' : '#d97706', borderRadius: '2px' }} />
          </div>
          <div style={{ fontSize: '10px', color: 'rgba(26,25,22,0.35)' }}>seuil {walletThreshold.toLocaleString('fr-FR')} €</div>
          <div style={{ marginTop: '8px', display: 'flex', gap: '5px' }}>
            <button onClick={e => { e.stopPropagation(); navigate('/wallet'); }} style={{ flex: 1, background: '#B8860B', color: '#fff', border: 'none', padding: '4px 0', borderRadius: '5px', fontSize: '10px', fontWeight: 500, cursor: 'pointer' }}>
              Recharger
            </button>
            <button onClick={e => { e.stopPropagation(); navigate('/wallet'); }} style={{ flex: 1, background: 'transparent', color: 'rgba(26,25,22,0.5)', border: '0.5px solid rgba(0,0,0,0.1)', padding: '4px 0', borderRadius: '5px', fontSize: '10px', cursor: 'pointer' }}>
              Configurer
            </button>
          </div>
        </div>
      </div>

      {/* Filtres */}
      <div style={{ display: 'flex', gap: '6px', marginBottom: '1rem' }}>
        {[
          { key: 'tous',        label: 'Tous' },
          { key: 'instruction', label: 'En instruction' },
          { key: 'valide',      label: 'Validés' },
          { key: 'rejete',      label: 'Rejetés' },
        ].map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)} style={{
            padding: '4px 12px', borderRadius: '999px', fontSize: '11px', cursor: 'pointer',
            border: '0.5px solid rgba(0,0,0,0.1)', fontFamily: 'DM Sans, sans-serif',
            background: filter === f.key ? '#1A1916' : 'transparent',
            color:      filter === f.key ? '#fff'    : 'rgba(26,25,22,0.5)',
          }}>{f.label}</button>
        ))}
      </div>

      {/* Table */}
      <div style={{ ...card, overflow: 'hidden' }}>
        {filtered.length === 0 ? (
          <div style={{ padding: '3rem', textAlign: 'center', color: 'rgba(26,25,22,0.35)', fontSize: '13px' }}>Aucun dossier dans ce statut.</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '0.5px solid rgba(0,0,0,0.07)' }}>
                {cols.map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '10px 14px', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.07em', color: 'rgba(26,25,22,0.28)', fontWeight: 400 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((d, i) => {
                const st = INPI_STATUS[d.status] || INPI_STATUS.recu;
                const sla = d.sla_deadline ? new Date(d.sla_deadline) : null;
                const now = new Date();
                const slaH = sla ? Math.round((sla - now) / 3600000) : null;
                const slaColor = slaH !== null ? (slaH < 0 ? '#B03020' : slaH < 6 ? '#d97706' : '#1A7A4A') : 'rgba(26,25,22,0.3)';
                const atts = attsByDossier[d.id];
                const kbisAtt = findAttByCategory(atts, 'Kbis');
                const rbeAtt  = findAttByCategory(atts, 'RBE');
                return (
                  <tr key={d.id} style={{ borderBottom: i < filtered.length - 1 ? '0.5px solid rgba(0,0,0,0.05)' : 'none' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#F5F3EE'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <td style={{ padding: '11px 14px', fontSize: '12px', fontWeight: 500, color: '#1A1916' }}>
                      <CopyText text={d.reference} mono />
                    </td>
                    <td style={{ padding: '11px 14px', fontSize: '12px', color: 'rgba(26,25,22,0.7)' }}>
                      <CopyText text={d.inpi_depot_id} mono />
                    </td>
                    <td style={{ padding: '11px 14px', fontSize: '12px', color: 'rgba(26,25,22,0.7)' }}>{d.societe_nom || '—'}</td>
                    <td style={{ padding: '11px 14px', fontSize: '12px', color: 'rgba(26,25,22,0.5)' }}>{d.type?.replace(/_/g, ' ')}</td>
                    {/* Date d'envoi */}
                    <td style={{ padding: '11px 14px', fontSize: '12px', color: 'rgba(26,25,22,0.5)' }}>
                      {fmtDate(dateEnvoi(d))}
                    </td>
                    {/* Déposé le */}
                    <td style={{ padding: '11px 14px', fontSize: '12px', color: 'rgba(26,25,22,0.45)' }}>
                      {fmtDate(dateDepot(d))}
                    </td>
                    {/* Statut */}
                    <td style={{ padding: '11px 14px' }}>
                      <span style={{ fontSize: '11px', fontWeight: 500, padding: '2px 8px', borderRadius: '4px', color: st.color, background: st.bg }}>{st.label}</span>
                    </td>
                    {/* Tarif GTC — éditable inline */}
                    <td style={{ padding: '11px 14px' }}>
                      <EditablePriceCell
                        value={d.tarif_gtc}
                        onSave={(v) => handleSaveTarif(d.id, 'tarif_gtc', v)}
                      />
                    </td>
                    {/* Tarif JAL — éditable inline */}
                    <td style={{ padding: '11px 14px' }}>
                      <EditablePriceCell
                        value={d.tarif_jal}
                        onSave={(v) => handleSaveTarif(d.id, 'tarif_jal', v)}
                      />
                    </td>
                    {/* Colonnes dynamiques selon onglet */}
                    {filter === 'valide' ? (
                      <>
                        <td style={{ padding: '11px 14px' }}>
                          <DocCell att={kbisAtt} />
                        </td>
                        <td style={{ padding: '11px 14px' }}>
                          <DocCell att={rbeAtt} />
                        </td>
                      </>
                    ) : filter === 'rejete' ? (
                      <td style={{ padding: '11px 14px', fontSize: '12px', color: 'rgba(176,48,32,0.85)', maxWidth: '280px', whiteSpace: 'pre-wrap', lineHeight: 1.35 }}>
                        {motifRejet(d) || <span style={{ color: 'rgba(26,25,22,0.3)' }}>—</span>}
                      </td>
                    ) : (
                      <td style={{ padding: '11px 14px', fontSize: '12px', fontWeight: 500, color: slaColor }}>
                        {slaH !== null ? (slaH < 0 ? 'Expiré' : slaH < 24 ? `${slaH}h` : `J+${Math.round(slaH/24)}`) : '—'}
                      </td>
                    )}
                    <td style={{ padding: '11px 14px' }}>
                      <button onClick={() => navigate('/dossiers')} style={{ background: 'transparent', border: '0.5px solid rgba(0,0,0,0.12)', borderRadius: '5px', padding: '3px 10px', fontSize: '11px', cursor: 'pointer', color: 'rgba(26,25,22,0.6)' }}>
                        Voir
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
